import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Překlady se NEimportují staticky — každý jazyk je samostatný lazy chunk a stáhne se jen
// ten, který stránka reálně potřebuje (viz loadLanguage). Dřív bylo všech 12 JSON (~1,1 MB)
// napevno v entry bundlu → zbytečně obří vstupní JS a pomalý FCP/LCP. Přepínač jazyka dělá
// plný reload (viz Layout.switchLang), takže na jedno načtení stačí 1 jazyk + cs fallback.
const loaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  cs: () => import('./locales/cs.json'),
  en: () => import('./locales/en.json'),
  de: () => import('./locales/de.json'),
  pl: () => import('./locales/pl.json'),
  sk: () => import('./locales/sk.json'),
  it: () => import('./locales/it.json'),
  hu: () => import('./locales/hu.json'),
  uk: () => import('./locales/uk.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  nl: () => import('./locales/nl.json'),
  ja: () => import('./locales/ja.json'),
}

const loadedLangs = new Set<string>()

/** Donačte překlady jednoho jazyka (idempotentní). Volá se před mountem (main.tsx) i při
 *  runtime přepnutí jazyka (languageChanged níže). Síťová chyba = tiše zůstane fallback cs. */
export async function loadLanguage(code: string): Promise<void> {
  const c = (code || 'cs').split('-')[0]
  if (loadedLangs.has(c) || !loaders[c]) return
  try {
    const mod = await loaders[c]()
    i18n.addResourceBundle(c, 'translation', mod.default ?? mod, true, true)
    loadedLangs.add(c)
  } catch {
    /* ignore — chybějící klíče spadnou na fallbackLng 'cs' */
  }
}

export const LANGUAGES = [
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
] as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Prázdné — jazyky se donačítají lazy přes loadLanguage (main.tsx před mountem).
    resources: {},
    fallbackLng: 'cs',
    interpolation: { escapeValue: false },
    // Bez Suspense: dokud není bundle jazyka načtený, chybějící klíč spadne na fallback 'cs'
    // (ten načítáme vždy první), žádný throw do Suspense. Při runtime přepnutí jazyka to drží UI.
    react: { useSuspense: false },
    detection: {
      // querystring (?lng=sk) má přednost — onboarding/jiná appka může předat jazyk;
      // pak localStorage (volba z přepínače), nakonec jazyk prohlížeče. Volba se uloží.
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      caches: ['localStorage'],
    },
  })

// Runtime přepnutí jazyka (i18n.changeLanguage v App.tsx) → dolaď jeho bundle, pokud chybí.
i18n.on('languageChanged', (lng) => { void loadLanguage(lng) })

export default i18n
